import { EyeIcon } from '@heroicons/react/outline';
import Button from 'components/atoms/Button';
import Divider from 'components/atoms/Divider';
import Heading from 'components/atoms/Heading';
import MultiSelectComboBox from 'components/atoms/MultiSelectComboBox';
import Radio from 'components/atoms/Radio';
import Text from 'components/atoms/Text';
import React, { useState } from 'react';
import { useAssignSurveySubmissionMutation } from 'redux/api/surveys';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { openCommentModal, openSurveyModal } from 'redux/slices/surveys';
import { IVehicleSurveySubmissions } from 'types/Surveys';
import { findDateDifference } from 'utils/findDateDifference';
import { formatDateTime } from 'utils/formatDateTime';

export interface ISurveyCard {
	className?: string;
	survey: IVehicleSurveySubmissions;
	refetchData: () => void;
}

const SurveyCard: React.FC<ISurveyCard> = ({ survey, refetchData }) => {
	const [assignUser] = useAssignSurveySubmissionMutation();
	const userPermission = useAppSelector((state) => state.user.permissions);
	const dispatch = useAppDispatch();
	const [status, setStatus] = useState(survey.survey_status || 'submitted');
	const [assignees, setAssignees] = useState(survey?.assignees || []);

	const hasWriteAccess = () => {
		const permission = userPermission?.filter(
			(permission) =>
				permission?.branch?.id === survey?.branch?.id &&
				permission.survey_level === 'write'
		);

		if (!permission) {
			return false;
		}

		return permission.length > 0;
	};

	const handleOpenComments = () => {
		const payload = {
			commentSurveyId: survey.id,
			surveyBranchId: survey?.branch?.id,
		};

		dispatch(openCommentModal(payload));
	};

	const handleSurveyResults = () => {
		dispatch(openSurveyModal(survey.uid));
	};

	const handleUpdateSurveySubmission = async () => {
		await assignUser({
			id: survey.id,
			body: {
				assignees: assignees,
				survey_status: status,
			},
		});

		refetchData();
	};

	return (
		<div className="w-full pb-10 pl-10 pr-10">
			<Divider />
			<div className="grid grid-cols-2 gap-10 my-6 space-y-2 grid-col xl:grid-cols-3">
				<div>
					<Heading type="h3" className="mb-6">
						Overview
					</Heading>
					<div className="flex flex-row gap-16 my-6">
						<Text className="2xl:min-w-[200px]">Summary Details</Text>
						<Text>{survey.survey_description ?? '-'}</Text>
					</div>
					<Divider />
					<div className="flex flex-row gap-16 my-6">
						<Text className="2xl:min-w-[200px]">Survey Type</Text>
						<Text>{survey?.survey.survey_type}</Text>
					</div>
					<Divider />
					<div className="flex flex-row gap-16 my-6">
						<Text className="2xl:min-w-[200px]">Completion Time</Text>
						<Text>
							{formatDateTime(survey.responded_time)} (time to action:{' '}
							{findDateDifference(survey.createdAt, survey.responded_time)})
						</Text>
					</div>
					<Divider />
					<Button
						size="lg"
						className="mt-6 font-bold"
						onClick={handleSurveyResults}
					>
						Survey Results
					</Button>
				</div>

				<div className="hidden xl:block">
					<Heading type="h3" className="mb-6">
						Details
					</Heading>
					<div className="flex flex-row gap-16 my-6">
						<Text className="2xl:min-w-[200px]">Job No.</Text>
						<Text>{survey.job_number ?? '-'}</Text>
					</div>
					<Divider />
					<div className="flex flex-row gap-16 my-6">
						<Text className="2xl:min-w-[200px]">Recipient</Text>
						<div>
							<Text>{`${survey.first_name} ${survey.last_name}`}</Text>
							<Text>{survey.email}</Text>
						</div>
					</div>
					<Divider />
					<div className="flex flex-row gap-16 my-6">
						<Text className="2xl:min-w-[200px]">Company</Text>
						<Text>{survey.company ?? '-'}</Text>
					</div>
					<Divider />
					<div className="flex flex-row gap-16 my-6">
						<Text className="2xl:min-w-[200px]">Created by</Text>
						<Text>
							{survey?.survey_created_by ?? 'Unknown User'} :{' '}
							{formatDateTime(survey.createdAt)}
						</Text>
					</div>
					<Divider />
				</div>

				<div>
					<div className="w-full max-w-sm">
						<Heading type="h3" className="mb-6">
							History
						</Heading>
						<Button
							type="quaternary"
							className="mr-5 font-bold"
							size="lg"
							onClick={handleOpenComments}
						>
							<EyeIcon className="w-5 h-5 mr-1" />
							View History
						</Button>
						{hasWriteAccess() && (
							<>
								<div className="h-0 border-t-[1px] border-primary-200 w-full my-5" />
								<Heading type="h2" className="mb-6">
									Assign to
								</Heading>

								<MultiSelectComboBox
									options={survey?.managers.map((manager) => {
										return {
											id: manager.id,
											label: `${manager.first_name} ${manager.last_name}`,
										};
									})}
									placeholder="Assign Users"
									selectedAssignees={assignees}
									setSelectedAssignees={setAssignees}
									customSelectMessage="users"
								/>
								<Heading type="h2" className="mt-4">
									Status
								</Heading>
								<Radio
									className="mt-5 mb-7"
									options={[
										{ title: 'Open', id: 'open' },
										{ title: 'Close', id: 'closed' },
									]}
									defaultId={status}
									onChange={setStatus}
									radioDirection="flex-row"
									classNameWrapper="gap-4"
								/>

								<Button
									navigate="next"
									className="font-bold"
									size="lg"
									onClick={handleUpdateSurveySubmission}
								>
									Submit
								</Button>
							</>
						)}
					</div>
				</div>
			</div>

			<div className="w-full mt-10 xl:hidden">
				<Heading type="h3">Details</Heading>
				<div className="grid grid-cols-2 gap-10 grid-col">
					<div>
						<div className="flex flex-row gap-16 my-6">
							<Text>Job No.</Text>
							<Text>{survey?.job_number ?? '-'}</Text>
						</div>
						<Divider />
						<div className="flex flex-row gap-16 my-6">
							<Text>Recipient</Text>
							<div>
								<Text>{`${survey.first_name} ${survey.last_name}`}</Text>
								<Text>{`${survey.email}`}</Text>
							</div>
						</div>
						<Divider />
					</div>

					<div>
						<div className="my-3">
							<Text>Created by</Text>
							<Text>
								{survey?.survey_created_by ?? 'Unknown User'} :{' '}
								{formatDateTime(survey.createdAt)}
							</Text>
						</div>
						<Divider />
						<div className="flex flex-row gap-16 my-6">
							<Text>Company</Text>
							<Text>{survey.company ?? '-'}</Text>
						</div>
						<Divider />
					</div>
				</div>
			</div>
		</div>
	);
};

export default SurveyCard;
